import { toast } from "sonner";
import { FileDown, RotateCw } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Loading, StatusBadge } from "@/components/common";
import { api, useFetch, errMsg, downloadFile } from "@/lib/api";

const monthLabel = (m) => new Date(`${m}-01T00:00:00`).toLocaleDateString("en-GB", { month: "long", year: "numeric" });
const when = (iso) => new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

export function StatementHistory({ landlord, onClose }) {
  const { data, reload } = useFetch(landlord ? `/biz/landlords/${landlord.id}/statements` : null);
  const resend = async (month) => {
    try {
      const r = await api.post(`/biz/landlords/${landlord.id}/statement/send`, { month });
      if (r.data.status === "sent") toast.success(`${monthLabel(month)} statement re-sent to ${r.data.to}`);
      else toast.warning("Email isn't set up yet — ask your administrator to configure SMTP.");
    } catch (e) { toast.error(errMsg(e)); }
    reload();
  };
  const download = (month) => downloadFile(`/biz/landlords/${landlord.id}/statement?month=${month}`,
    `statement-${landlord.name.replace(/\s+/g, "-").toLowerCase()}-${month}.pdf`).catch((e) => toast.error(errMsg(e)));
  return (
    <Sheet open={!!landlord} onOpenChange={(o) => !o && onClose()}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto" data-testid="statement-history-sheet">
        <SheetHeader>
          <SheetTitle className="font-display text-2xl text-left">Statement history</SheetTitle>
          <SheetDescription className="text-left">{landlord?.name} · {landlord?.email || "no email"}</SheetDescription>
        </SheetHeader>
        {!data ? <Loading /> : data.length === 0 ? (
          <p className="text-sm text-slate-500 mt-8" data-testid="statement-history-empty">No statements have been emailed to this landlord yet.</p>
        ) : (
          <ul className="mt-6 divide-y divide-slate-100">
            {data.map((s) => (
              <li key={s.id} className="py-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between" data-testid={`statement-log-${s.id}`}>
                <div>
                  <div className="font-semibold">{monthLabel(s.month)}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{when(s.created_at)} · to {s.to}</div>
                  <div className="text-xs text-slate-500">{s.source === "auto" ? "Sent automatically" : `Sent by ${s.sent_by}`}</div>
                </div>
                <div className="flex items-center gap-2">
                  <StatusBadge status={s.status} label={s.status === "skipped" ? "not sent" : s.status} />
                  <Button size="icon" variant="ghost" onClick={() => download(s.month)} title="Download PDF" data-testid={`statement-log-download-${s.id}`}><FileDown className="w-4 h-4" /></Button>
                  <Button size="sm" variant="outline" onClick={() => resend(s.month)} disabled={!landlord.email} data-testid={`statement-log-resend-${s.id}`}><RotateCw className="w-4 h-4 mr-1" />Resend</Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </SheetContent>
    </Sheet>
  );
}
